import { useEffect, useRef, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiUrl, mockAuthHeaders } from '../lib/api';

export interface WebSocketMessage {
  type: string;
  cursor?: string;
  orderId?: string;
  projectId?: string;
  taskId?: string;
  alertId?: string;
  [key: string]: unknown;
}

export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error' | 'unauthenticated';

interface UseWebSocketOptions {
  enabled?: boolean;
  onMessage?: (message: WebSocketMessage) => void;
}

export function useWebSocket(channels: string[], options: UseWebSocketOptions = {}) {
  const { enabled = true, onMessage } = options;
  const wsRef = useRef<WebSocket | null>(null);
  const queryClient = useQueryClient();
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [lastConnectedAt, setLastConnectedAt] = useState<Date | null>(null);
  const reconnectTimeoutRef = useRef<number>();
  const reconnectAttempts = useRef(0);
  const channelsRef = useRef(channels);
  const isConnectingRef = useRef(false);

  // Update channels ref when they change
  useEffect(() => {
    channelsRef.current = channels;
  }, [channels]);

  const connect = useCallback(async () => {
    if (!enabled) return;

    // Guard against multiple connections (React strict mode, etc.)
    if (wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    // Guard against multiple simultaneous connection attempts during async ticket fetch
    if (isConnectingRef.current) return;
    isConnectingRef.current = true;

    try {
      setConnectionState('connecting');

      // Get WebSocket ticket from API
      const ticketUrl = apiUrl('/api/v1/ws-ticket');
      const ticketRes = await fetch(ticketUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...mockAuthHeaders(),
        },
        credentials: 'include',
      });

      if (ticketRes.status === 401) {
        // Authentication failed - don't retry, user needs to re-login
        console.warn('[WebSocket] Authentication failed (401) - not retrying');
        setConnectionState('unauthenticated');
        isConnectingRef.current = false;
        return;
      }

      if (!ticketRes.ok) {
        throw new Error(`Failed to get WebSocket ticket: ${ticketRes.status}`);
      }

      const ticketJson = await ticketRes.json();
      const ticket = ticketJson.data?.ticket;
      if (!ticket) {
        throw new Error('Invalid WebSocket ticket response');
      }

      // Connect to WebSocket on API host
      // Use VITE_API_URL to get the host for WebSocket connection
      const apiBaseUrl = import.meta.env.VITE_API_URL;
      let apiHost = window.location.host;

      if (apiBaseUrl) {
        try {
          apiHost = new URL(apiBaseUrl).host;
        } catch (err) {
          console.error('Invalid VITE_API_URL for WebSocket:', apiBaseUrl, err);
          // Falls through to use window.location.host
        }
      } else if (!import.meta.env.DEV) {
        console.warn('VITE_API_URL not configured; WebSocket using window.location.host as fallback');
      }

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${apiHost}/ws?ticket=${ticket}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setConnectionState('connected');
        setLastConnectedAt(new Date());
        reconnectAttempts.current = 0;

        // Subscribe to channels
        channelsRef.current.forEach((channel) => {
          ws.send(JSON.stringify({ type: 'subscribe', channel }));
        });
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage;
          setLastMessage(message);

          // Call custom message handler if provided
          onMessage?.(message);

          // Handle message types with query invalidation
          switch (message.type) {
            case 'schedule_updated':
              queryClient.invalidateQueries({ queryKey: ['schedule'] });
              queryClient.invalidateQueries({ queryKey: ['tasks'] });
              queryClient.invalidateQueries({ queryKey: ['priority'] });
              break;

            case 'my_tasks_updated':
              queryClient.invalidateQueries({ queryKey: ['tasks'] });
              break;

            case 'alert_created':
            case 'alert_updated':
              queryClient.invalidateQueries({ queryKey: ['alerts'] });
              queryClient.invalidateQueries({ queryKey: ['alertCounts'] });
              break;

            case 'order_updated':
              queryClient.invalidateQueries({ queryKey: ['orders'] });
              if (message.orderId) {
                queryClient.invalidateQueries({ queryKey: ['order', message.orderId] });
              }
              break;

            case 'line_item_scheduled':
              queryClient.invalidateQueries({ queryKey: ['schedule'] });
              queryClient.invalidateQueries({ queryKey: ['tasks'] });
              break;

            case 'ping':
              ws.send(JSON.stringify({ type: 'pong' }));
              break;

            case 'subscribed':
              // Successfully subscribed to channel
              break;

            case 'error':
              console.error('WebSocket error message:', message);
              break;

            case 'cursor_expired':
              // Need to re-fetch full state
              queryClient.invalidateQueries();
              break;
          }
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      ws.onclose = (event) => {
        setConnectionState('disconnected');
        wsRef.current = null;

        // Don't reconnect if closed cleanly (code 1000) or if disabled
        if (event.code === 1000 || !enabled) return;

        // Exponential backoff reconnect with jitter
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        const jitter = Math.random() * 1000;
        reconnectAttempts.current++;

        if (reconnectAttempts.current < 10) {
          reconnectTimeoutRef.current = window.setTimeout(connect, delay + jitter);
        } else {
          setConnectionState('error');
        }
      };

      ws.onerror = () => {
        setConnectionState('error');
        ws.close();
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('[WebSocket] Connection failed:', error);
      setConnectionState('error');

      // Retry after delay with exponential backoff
      if (reconnectAttempts.current < 10) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
        const jitter = Math.random() * 1000;
        reconnectAttempts.current++;
        console.log(`[WebSocket] Scheduling retry ${reconnectAttempts.current}/10 in ${Math.round(delay + jitter)}ms`);
        reconnectTimeoutRef.current = window.setTimeout(connect, delay + jitter);
      } else {
        console.warn('[WebSocket] Max retries reached, stopping');
      }
    } finally {
      isConnectingRef.current = false;
    }
  }, [enabled, onMessage, queryClient]);

  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
        wsRef.current = null;
      }
      isConnectingRef.current = false;
    };
  }, [connect, enabled]);

  // Reconnect with new channels
  const reconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'Reconnecting');
    }
    reconnectAttempts.current = 0;
    connect();
  }, [connect]);

  return {
    connectionState,
    connected: connectionState === 'connected',
    lastMessage,
    lastConnectedAt,
    reconnect,
  };
}
